import React, { FormEventHandler } from 'react';
import RetailFormInput from 'components/retail/structures/RetailFormInput';
import RetailFormLabel from 'components/retail/structures/RetailFormLabel';
import { Box, Hidden } from '@material-ui/core';
import RetailFormDivider from 'components/retail/structures/RetailFormDivider';
import { NextArrow } from 'assets/svgs';
import RetailFormButton from 'components/retail/structures/RetailFormButton';
import { observer } from 'mobx-react-lite';
import RetailFormTitle from 'components/retail/structures/RetailFormTitle';
import RetailCompareEnergyFormCard from 'components/retail/structures/RetailCompareEnergyFormCard';
import { ProgressSpinner } from 'components/common/atoms';
import { RetailGreenCheckbox, RetailPhoneFormat } from "components/retail/structures";
import CompareRatesStore2 from 'stores/retail/CompareRatesStore2';
import { ServerError } from 'components/common/structures';
import { Plan } from 'services/quotes/types';
import services from 'services';
import { Commodity } from 'services/delivery/types';
import { CustomerType } from 'services/orders/types';
import { RetailLocation } from 'evcharging/services/types';
import { getRetailLocationByZipCode } from 'evcharging/services/cmsClient';
import { RETAIL_LEAD_FORM_VALUES_KEY } from "config/retail";
import useI18n from "evcharging/hooks/useI18n";

const compareRatesStore = new CompareRatesStore2();

export interface CompareRatesResult {
  plan: Plan;
  zipCode: string;
  wasFound: boolean;
  retailLocation?: RetailLocation;
}

export interface CompareRatesFormProps {
  onSubmitForm: (result: CompareRatesResult) => void;
}

const getPlanByZipCode = async (zipCode: string): Promise<CompareRatesResult> => {
  const { quotesService, deliveryService } = services;
  const request = {
    commodity: Commodity.ELECTRICITY,
    customerType: CustomerType.COMMERCIAL,
  };

  try {
    const { state } = await deliveryService.getZipCode(zipCode);
    const plan = await quotesService.getBestPrice({ ...request, state });
    if (!plan) {
      return { plan: await quotesService.getBestPrice(request), zipCode, wasFound: false };
    }
    const retailLocation = await getRetailLocationByZipCode(zipCode);
    return { plan, zipCode, wasFound: !!retailLocation, retailLocation };
  } catch (e) {
    return { plan: await quotesService.getBestPrice(request), zipCode, wasFound: false };
  }
};

const CompareRatesForm2 = observer((props: CompareRatesFormProps) => {
  const { onSubmitForm } = props;

  const { t } = useI18n();

  const {
    onChange,
    changeZipCode,
    onSubmit,
    getForm,
    isValid,
    data,
    getErrorMessage,
    closeError,
    disableConfirm,
  } = compareRatesStore;
  const values = getForm;

  const emailHandler = onChange('email');

  const onChangeEmail = (value: string) => {
    emailHandler(value.trim());
  };

  const onHandleSubmit: FormEventHandler = async (event) => {
    event.preventDefault();
    compareRatesStore.data.isLoading = true;
    const result = await getPlanByZipCode(values.zipCode);
    localStorage.setItem(RETAIL_LEAD_FORM_VALUES_KEY, JSON.stringify({...values}));
    onSubmitForm(result);
    return onSubmit();
  };

  return (
    <form onSubmit={onHandleSubmit}>
      <RetailCompareEnergyFormCard>
        <RetailFormTitle>{t('compare-rates.check-rates')}</RetailFormTitle>

        <RetailFormLabel>{t('form.label.zipCode')}*</RetailFormLabel>
        <RetailFormInput
          placeholder={t('form.label.message-placeholder')}
          onChange={changeZipCode()}
          value={values.zipCode}
        />

        <Box mb={4} />
        <RetailFormLabel>{t('form.label.phone')}*</RetailFormLabel>
        <RetailFormInput
          placeholder={t('form.label.message-placeholder')}
          onChange={onChange('phone')}
          value={values.phone}
          InputProps={{ inputComponent: RetailPhoneFormat }}
        />

        <Box mb={4} />
        <RetailFormLabel>{t('form.label.email')}*</RetailFormLabel>
        <RetailFormInput placeholder={t('form.label.message-placeholder')} onChange={onChangeEmail} value={values.email} />

        <RetailGreenCheckbox
          label={t('form.label.agree-receive')}
          onChange={(event) => {
            onChange('agreeToReceive')(event.target.checked ? 'yes' : 'no');
          }}
          checked={values.agreeToReceive === 'yes'}
        />

        <Hidden smUp>
          <Box my={4} />
        </Hidden>

        <Hidden xsDown>
          <RetailFormDivider />
        </Hidden>

        {data.isLoading && <ProgressSpinner />}
        {data.isLoading || (
          <RetailFormButton disabled={!isValid} type={'submit'}>
            {t('compare-rates.get-best-rates')}
            <NextArrow />
          </RetailFormButton>
        )}
      </RetailCompareEnergyFormCard>
      <ServerError
        onClose={disableConfirm}
        reverseColor
        newStyle
        message={!data.isConfirm && t('compare-rates.request-submitted')}
      />
      <ServerError onClose={closeError} message={getErrorMessage} />
    </form>
  );
});

export default CompareRatesForm2;
